import * as S from './styles'
import { Select, Modal, OptionProps, Checkbox, Info } from 'components'
import { useSelector, useDispatch } from 'store/store'
import { Controller, useFormContext, useFormState } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { asyncGetMaterialById, MaterialByIdResponse } from 'store/ProductContext'
import { useState } from 'react'
import { useCallbackPrompt } from 'commons/customHooks/useCallbackPrompt'
import { TypesValidations } from 'constants/enums'
import { setShowDetailedStock } from 'store/StructuralContext/structuralSlice'
import { AbortResetClient } from 'commons/customHooks'
import { QuotaLevel, TypeRuleExceptionStock, YesNoNotApplicable } from 'constants/enums'

const FactoryStock = ({ resetController }: AbortResetClient) => {
  const { control } = useFormContext()
  const dispatch = useDispatch()
  const [factoryModal, setFactoryModal] = useState(false)
  const [selectedFactory, setSelectedFactory] = useState('')
  const { isDirty } = useFormState()
  useCallbackPrompt(isDirty)
  const { orderNumber, materialNumber } = useParams()
  const { showDetailedStock } = useSelector((state) => state.structure)
  const { materialSelected, defaultParams } = useSelector((state) => state.products)
  const {
    draftBody: { dt_dsjd_pedi }
  } = useSelector((state) => state.salesOrder)

  const {
    qt_cota_disp_gnv,
    qt_cota_disp_grv,
    qt_cota_disp_zona,
    centros,
    fl_estq_lvre,
    fl_pivo_lvre,
    res_cent_matr,
    tp_estq_exce,
    tp_bloq_pivo
  } = materialSelected

  const isOutOfLine = defaultParams.fl_matr_fora_linh === TypesValidations.YES

  const getFactoryOptions = () => {
    if (
      !centros.find((factory) => factory.cd_cent_matr === res_cent_matr) &&
      !!res_cent_matr.trim()
    ) {
      const factoryOptions: MaterialByIdResponse['centros'] = [
        ...centros,
        { cd_cent_matr: res_cent_matr }
      ]
      return setFactoryOptions(factoryOptions)
    }
    return setFactoryOptions(centros)
  }

  const setFactoryOptions = (centers: MaterialByIdResponse['centros']): OptionProps[] => {
    const newCentros = centers.map((factory) => ({
      title: factory.cd_cent_matr,
      value: factory.cd_cent_matr
    }))
    const todos = {
      title: 'Todos',
      value: ''
    }
    if (materialSelected?.fl_pedi_antp === TypesValidations.YES) {
      newCentros.unshift(todos)
    }
    return newCentros
  }

  const handleFactoryChange = (value: string) => {
    const { controller } = resetController()

    dispatch(
      asyncGetMaterialById(
        {
          ...defaultParams,
          cd_matr: materialNumber,
          nr_pedi_rasc: orderNumber,
          cd_cent_matr: value,
          dt_dsjd_pedi
        },
        '1',
        controller.signal
      )
    )
  }

  const getStockType = (tpEstqExce: MaterialByIdResponse['tp_estq_exce']) => {
    if (tpEstqExce.includes(TypeRuleExceptionStock.MATERIAL)) return 'Material'
    if (tpEstqExce.includes(TypeRuleExceptionStock.COR)) return 'Cor'
    if (tpEstqExce.includes(TypeRuleExceptionStock.SKU)) return 'SKU'
    if (tpEstqExce.includes(TypeRuleExceptionStock.CONTRAESTOQUE)) return 'Contra estoque'
    return 'SKU'
  }

  const renderFactorySelect = () => {
    return (
      <S.FactorySelect>
        <S.FactoryLabel>Centro</S.FactoryLabel>
        <S.FactorySelectWrapper>
          <Controller
            name="cd_cent_matr"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Select
                name={name}
                value={value}
                id="factory-centro"
                options={getFactoryOptions()}
                required={!!centros.length}
                disabled={!centros.length || !getFactoryOptions()[0].title.trim()}
                defaultValue={getFactoryOptions()[0].value}
                onChange={(e) => {
                  const valueCdCentMatr = e.target?.value

                  if (typeof valueCdCentMatr !== 'string') return

                  if (isDirty) {
                    setSelectedFactory(valueCdCentMatr)
                    setFactoryModal(true)
                  } else {
                    handleFactoryChange(valueCdCentMatr)
                    onChange(e)
                  }
                }}
              />
            )}
          />
        </S.FactorySelectWrapper>
        <S.StockCheckbox>
          <Checkbox
            checked={showDetailedStock}
            label="Detalhar Estoque"
            size="small"
            onChange={(e) => {
              dispatch(setShowDetailedStock(e.target.checked))
            }}
          />
          <Info iconSize={15} placement="top-end">
            Ao ativar este campo, você poderá visualizar o total de materiais disponíveis.
          </Info>
        </S.StockCheckbox>
      </S.FactorySelect>
    )
  }

  return (
    <>
      {isOutOfLine ? (
        renderFactorySelect()
      ) : (
        <S.Container>
          <S.QuotaWrapper>
            <S.QuotaContainer>
              {materialSelected?.fl_pedi_antp !== TypesValidations.YES && (
                <>
                  <S.QuotaInfoContainer
                    activated={tp_bloq_pivo.includes(QuotaLevel.Zone)}
                  >
                    <S.QuotaTitle>Cota Zona</S.QuotaTitle>
                    <S.QuotaValue>{qt_cota_disp_zona.toLocaleString()}</S.QuotaValue>
                  </S.QuotaInfoContainer>
                  <S.QuotaInfoContainer
                    activated={tp_bloq_pivo.includes(QuotaLevel.Regional)}
                  >
                    <S.QuotaTitle>Cota GRV</S.QuotaTitle>
                    <S.QuotaValue>{qt_cota_disp_grv.toLocaleString()}</S.QuotaValue>
                  </S.QuotaInfoContainer>
                  <S.QuotaInfoContainer
                    activated={tp_bloq_pivo.includes(QuotaLevel.National)}
                  >
                    <S.QuotaTitle>Cota GNV</S.QuotaTitle>
                    <S.QuotaValue>{qt_cota_disp_gnv.toLocaleString()}</S.QuotaValue>
                  </S.QuotaInfoContainer>
                  <S.QuotaInfoContainer>
                    <S.QuotaTitle>Cota Livre</S.QuotaTitle>
                    <S.QuotaValue>
                      {fl_pivo_lvre === YesNoNotApplicable.NotApplicable
                        ? YesNoNotApplicable.NO
                        : fl_pivo_lvre}
                    </S.QuotaValue>
                  </S.QuotaInfoContainer>
                  <S.QuotaInfoContainer>
                    <S.QuotaTitle>Estoque Livre</S.QuotaTitle>
                    <S.QuotaValue>
                      {fl_estq_lvre === YesNoNotApplicable.NotApplicable
                        ? YesNoNotApplicable.NO
                        : fl_estq_lvre}
                    </S.QuotaValue>
                  </S.QuotaInfoContainer>
                  <S.QuotaInfoContainer>
                    <S.QuotaTitle>Regra de Exceção do Estoque</S.QuotaTitle>
                    <S.QuotaValue>{getStockType(tp_estq_exce)}</S.QuotaValue>
                  </S.QuotaInfoContainer>
                </>
              )}
            </S.QuotaContainer>
            {renderFactorySelect()}
          </S.QuotaWrapper>
        </S.Container>
      )}
      <Modal
        open={factoryModal}
        setOpen={setFactoryModal}
        title="Alterar Fábrica/Centro"
        description="Ao alterar a Fábrica/Centro, os dados digitados serão perdidos. Deseja continuar?"
        showReturn
        returnIcon={<></>}
        returnText="Não"
        buttonText="Sim"
        buttonAction={() => handleFactoryChange(selectedFactory)}
        returnAction={() => setFactoryModal(false)}
      />
    </>
  )
}

export default FactoryStock
